/**
 * Canonical seasonality token parsing (vocabulary + import aliases + sort order).
 * Shared by API coercion/validation and the admin recipe editor (via `com` package).
 */
import errors from './errors.js'
import { getRecipeImportAliasValue, getRecipeVocabularyValues } from './recipeVocabularies.js'

const { ValidationError } = errors

const ALLOWED_SEASONALITIES = getRecipeVocabularyValues('seasonalities')
const SEASONALITY_SORT_ORDER = [...ALLOWED_SEASONALITIES]

function trimPart(value) {
    if (value === null || value === undefined) return null
    const t = String(value).trim()
    return t.length > 0 ? t : null
}

/**
 * Flatten DB/API shapes into raw string segments (before alias + vocabulary).
 * Pipe inside array elements is split (defensive for bad clients).
 */
export function expandSeasonalityRawParts(rawValue) {
    if (rawValue === null || rawValue === undefined) return []
    if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
        throw new ValidationError('seasonality must be a string, string array, or null')
    }
    const out = []
    if (Array.isArray(rawValue)) {
        for (const entry of rawValue) {
            const s = trimPart(entry)
            if (!s) continue
            if (s.includes('|')) {
                out.push(...s.split('|').map((p) => p.trim()).filter(Boolean))
            } else {
                out.push(s)
            }
        }
        return out
    }
    if (typeof rawValue === 'string') {
        const t = rawValue.trim()
        if (!t) return []
        return t.includes('|') ? t.split('|').map((p) => p.trim()).filter(Boolean) : [t]
    }
    throw new ValidationError('seasonality must be a string, string array, or null')
}

/** Map one raw segment to canonical token via import aliases, or null if unknown. */
export function resolveSeasonalityToken(part) {
    const trimmed = trimPart(part)
    if (!trimmed) return null
    const mapped = getRecipeImportAliasValue('seasonalities', trimmed) || trimmed
    return ALLOWED_SEASONALITIES.includes(mapped) ? mapped : null
}

/** Stable display order matching controlled vocabulary order. */
export function sortSeasonalityTokens(tokens) {
    const rank = new Map(SEASONALITY_SORT_ORDER.map((v, i) => [v, i]))
    return [...tokens].sort((a, b) => (rank.get(a) ?? 99) - (rank.get(b) ?? 99))
}
